import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {colors} from '../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {SendIcon} from '../icon/svg/send-icon';

import styles from './prompt-box.module.css';

interface Props {
    placeholder?: string;
    onSubmit: () => void;
    value: string;
    onChange: (value: string) => void;
    autoFocus?: boolean;
    disabled?: boolean;
    tooltipText?: string;
    getTextareaRef?: (ref?: HTMLTextAreaElement | null | undefined) => void;
}

const PROMPT_BOX_MAX_HEIGHT = 150;
const MIN_HEIGHT = 37; // Minimum height for empty textarea (line-height 17px + padding 20px)

export function PromptBox(props: Props) {
    const {
        placeholder,
        onSubmit,
        value,
        onChange,
        autoFocus,
        disabled = false,
        getTextareaRef,
    } = props;

    // Determines if the textarea and submit button should share the same container
    // or be 'stacked'(with each having their own container stacked on top of each other)
    // We want them to share the same container when there is only one line of text but once the text starts hitting the button, we want to separate them.
    const [isStacked, setIsStacked] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement | null | undefined>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLSpanElement | null>(null);

    const setTextareaRef = (ref?: HTMLTextAreaElement | null) => {
        textareaRef.current = ref;
        if (getTextareaRef) {
            getTextareaRef(ref);
        }
    };

    useEffect(() => {
        ReactTooltip.rebuild();

        return () => {
            ReactTooltip.hide();
        };
    }, []);

    const adjustHeight = useCallback((textValue: string) => {
        const textarea = textareaRef.current;
        if (textarea) {
            // Reset to auto to measure
            textarea.style.height = 'auto';
            textarea.style.overflowY = 'hidden';

            // If empty, use min height
            if (!textValue || textValue.length === 0) {
                textarea.style.height = `${MIN_HEIGHT}px`;

                return;
            }

            if (textarea.scrollHeight > PROMPT_BOX_MAX_HEIGHT) {
                textarea.style.height = `${PROMPT_BOX_MAX_HEIGHT}px`;
                textarea.style.overflowY = 'auto';
            } else {
                textarea.style.height = `${Math.max(textarea.scrollHeight, MIN_HEIGHT)}px`;
            }
        }
    }, []);

    const calculateLayout = useCallback(
        (textValue: string) => {
            const textarea = textareaRef.current;
            const container = containerRef.current;
            const measureSpan = measureRef.current;

            if (!textarea || !container || !measureSpan) {
                return;
            }

            if (!textValue || textValue.length === 0) {
                setIsStacked(false);

                return;
            }

            if (textValue.includes('\n')) {
                setIsStacked(true);

                return;
            }

            // Measure text width using hidden span
            measureSpan.textContent = textValue.replace(/\n/g, ' ');
            const textWidth = measureSpan.offsetWidth;

            // Get container width and calculate row-mode available width
            const containerWidth = container.offsetWidth;
            const buttonWidth = 60; // 40px button + gap + padding
            const rowModeAvailableWidth = containerWidth - buttonWidth;

            // Determine if should be stacked
            const shouldStack = textWidth >= rowModeAvailableWidth - 20;
            setIsStacked(shouldStack);
            adjustHeight(textValue);
        },
        [adjustHeight]
    );

    // Calculate layout on mount and when value changes
    useLayoutEffect(() => {
        calculateLayout(value);
    }, [calculateLayout, value]);

    // Re-adjust height after layout changes (stacked/unstacked)
    useLayoutEffect(() => {
        adjustHeight(value);
    }, [isStacked, adjustHeight, value]);

    useLayoutEffect(() => {
        if (autoFocus && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(
                textareaRef.current.value.length,
                textareaRef.current.value.length
            );
        }
    }, [autoFocus, textareaRef]);

    // Submit when user presses command + enter
    // @ts-expect-error
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !disabled && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
        }
    };

    const containerStyleName = classnames(styles['prompt-box-container'], {
        [styles['prompt-box-container--stacked']]: isStacked,
        [styles['prompt-box-container--disabled']]: disabled,
    });

    return (
        <div ref={containerRef} className={containerStyleName}>
            {/* Hidden span for measuring text width. Needed to know when the text hits the button when they share the same container */}
            <span ref={measureRef} aria-hidden='true' className={styles['measure-span']} />
            <textarea
                ref={setTextareaRef}
                rows={1}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                className={styles['prompt-box-textarea']}
            />
            <div
                data-tip={props.tooltipText}
                className={styles['prompt-box-send-button-container']}
            >
                <button
                    onClick={onSubmit}
                    disabled={disabled}
                    className={styles['prompt-box-send-button']}
                >
                    <SendIcon size={16} color={colors.white} />
                </button>
            </div>
        </div>
    );
}
